import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import { motion } from "framer-motion";
import Swal from "sweetalert2";

import formatCurrency from "../shared/formatCurrency";
import { useAuth } from "../contexts/AuthContext";
import LoadingSpinner from "../components/LoadingSpinner";
import { fetchMembers } from "../services/memberService";
import {
  approveUpatuDisbursement,
  fetchUpatuDisbursements,
  initiateUpatuDisbursement,
  rejectUpatuDisbursement,
} from "../services/upatuDisbursementService";

const brand = "#800b33";
const brandHover = "#6c0a2b";

const statusMeta = {
  PENDING_CHAIR_APPROVAL: { label: "Waiting for Mwenyekiti", color: "default", step: 0 },
  PENDING_SECRETARY_APPROVAL: { label: "Waiting for Katibu", color: "info", step: 1 },
  PENDING_ACCOUNTANT_APPROVAL: { label: "Waiting for Muhasibu", color: "warning", step: 2 },
  APPROVED: { label: "Ready to Disburse", color: "success", step: 3 },
  REJECTED: { label: "Rejected", color: "error", step: 3 },
};

const nextStage = {
  PENDING_CHAIR_APPROVAL: "PENDING_SECRETARY_APPROVAL",
  PENDING_SECRETARY_APPROVAL: "PENDING_ACCOUNTANT_APPROVAL",
  PENDING_ACCOUNTANT_APPROVAL: "APPROVED",
};

const stageStatusByRole = {
  Mwenyekiti: "PENDING_CHAIR_APPROVAL",
  Katibu: "PENDING_SECRETARY_APPROVAL",
  Muhasibu: "PENDING_ACCOUNTANT_APPROVAL",
};

const chainSteps = ["Mwenyekiti", "Katibu", "Muhasibu", "Settlement"];

const fadeIn = {
  hidden: { opacity: 0, y: 16 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.05, duration: 0.35, ease: "easeOut" },
  }),
};

const initialRequests = [];

const StatCard = ({ title, value, subtitle, icon, index, accent = brand }) => (
  <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={index}>
    <Card sx={{ borderRadius: 3, boxShadow: 3, height: "100%" }}>
      <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            background: `${accent}1A`,
            color: accent,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="overline" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  </motion.div>
);

const UpatuDisbursements = () => {
  const { user } = useAuth();
  const role = user?.role;

  const [requests, setRequests] = useState(initialRequests);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [initiateOpen, setInitiateOpen] = useState(false);
  const [initiateForm, setInitiateForm] = useState({
    memberId: "",
    amount: "",
  });
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actioningId, setActioningId] = useState(null);

  const myStageStatus = stageStatusByRole[role];
  const isLeadership = ["Mwenyekiti", "Katibu", "Muhasibu"].includes(role);

  const normalizeRequest = (item) => {
    const reference = item.id ? `UP-${String(item.id).padStart(4, "0")}` : item.id;
    const history = [];
    if (item.created_at) {
      history.push({
        action: "Submitted",
        by: item.initiated_by_name || "Mwenyekiti",
        role: "Mwenyekiti",
        at: item.created_at,
      });
    }
    if (item.chair_approved_by_name) {
      history.push({
        action: "Approved",
        by: item.chair_approved_by_name,
        role: "Mwenyekiti",
        at: item.updated_at || item.created_at,
        note: "Forwarded to Katibu",
      });
    }
    if (item.secretary_approved_by_name) {
      history.push({
        action: "Approved",
        by: item.secretary_approved_by_name,
        role: "Katibu",
        at: item.updated_at || item.created_at,
        note: "Forwarded to Muhasibu",
      });
    }
    if (item.accountant_approved_by_name) {
      history.push({
        action: item.status === "REJECTED" ? "Rejected" : "Approved",
        by: item.accountant_approved_by_name,
        role: "Muhasibu",
        at: item.settled_at || item.updated_at || item.created_at,
        note: item.status === "REJECTED" ? item.rejection_reason : "Funds released",
      });
    }
    if (item.status === "REJECTED" && !history.find((h) => h.action === "Rejected")) {
      history.push({
        action: "Rejected",
        by: "Workflow",
        role: "-",
        at: item.updated_at || item.created_at,
        note: item.rejection_reason,
      });
    }

    return {
      id: item.id,
      reference,
      cycleName: item.group_name || "Upatu",
      member: item.target_member_name || "Member",
      requestedAt: item.created_at,
      amount: Number(item.amount || 0),
      status: item.status,
      targetAccount: item.target_member_account || "Account on file",
      settledAt: item.settled_at,
      rejectionReason: item.rejection_reason,
      history,
      raw: item,
    };
  };

  const loadMembers = async () => {
    try {
      const data = await fetchMembers();
      setMembers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadRequests = async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const data = await fetchUpatuDisbursements();
      const normalized = Array.isArray(data) ? data.map(normalizeRequest) : [];
      setRequests(normalized);
    } catch (err) {
      setError(err.message || "Failed to load payouts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isLeadership) {
      loadRequests();
      if (role === "Mwenyekiti") {
        loadMembers();
      }
    } else {
      setLoading(false);
    }
  }, [role]);

  const filteredRequests = useMemo(() => {
    const q = search.trim().toLowerCase();
    return requests.filter((req) => {
      const matchesQuery =
        !q ||
        String(req.reference || req.id || "").toLowerCase().includes(q) ||
        req.member.toLowerCase().includes(q) ||
        req.cycleName.toLowerCase().includes(q);
      const matchesStatus = statusFilter === "ALL" || req.status === statusFilter;
      return matchesQuery && matchesStatus;
    });
  }, [requests, search, statusFilter]);

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRequests.slice(start, start + rowsPerPage);
  }, [filteredRequests, page, rowsPerPage]);

  const memberOptions = useMemo(() => {
    return members.map((m) => ({
      id: m.id,
      name: m.full_name || m.user?.full_name || "Member",
      account: m.nmb_account_number,
    }));
  }, [members]);

  const counts = useMemo(() => {
    return requests.reduce(
      (acc, r) => {
        acc.total += 1;
        acc.inMyQueue += myStageStatus && r.status === myStageStatus ? 1 : 0;
        acc.readyToDisburse += r.status === "APPROVED" ? 1 : 0;
        acc.rejected += r.status === "REJECTED" ? 1 : 0;
        return acc;
      },
      { total: 0, inMyQueue: 0, readyToDisburse: 0, rejected: 0 }
    );
  }, [requests, myStageStatus]);

  const canAct = (request) => {
    if (!myStageStatus) return false;
    if (request.status === "REJECTED" || request.status === "APPROVED") return false;
    return request.status === myStageStatus;
  };

  const openDetails = (request) => {
    setSelectedRequest(request);
    setDialogOpen(true);
  };

  const closeDetails = () => {
    setDialogOpen(false);
    setSelectedRequest(null);
  };

  const appendHistory = (request, entry) => {
    return { ...request, history: [...request.history, entry] };
  };

  const handleApprove = async (request) => {
    const nextStatus = nextStage[request.status];
    const nextRole =
      request.status === "PENDING_CHAIR_APPROVAL"
        ? "Katibu"
        : request.status === "PENDING_SECRETARY_APPROVAL"
          ? "Muhasibu"
          : "Settlement";

    const confirmation = await Swal.fire({
      icon: "question",
      title: "Approve Upatu payout?",
      text: `This will forward to ${nextRole}.`,
      showCancelButton: true,
      confirmButtonColor: brand,
      cancelButtonColor: "#9e9e9e",
      confirmButtonText: "Approve and forward",
    });
    if (!confirmation.isConfirmed) return;

    setActioningId(request.id);
    try {
      const updated = await approveUpatuDisbursement(request.id);
      setSelectedRequest((prev) => (prev && prev.id === request.id ? normalizeRequest(updated) : prev));
      await loadRequests(true);
      Swal.fire({
        icon: "success",
        title: "Action captured",
        text: `Sent to ${nextRole}.`,
        confirmButtonColor: brand,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Unable to approve",
        text: err.message || "Please try again.",
        confirmButtonColor: brand,
      });
    } finally {
      setActioningId(null);
    }
  };

  const handleReject = async (request) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Reject this payout?",
      text: "Share a short reason for the audit log.",
      input: "textarea",
      inputPlaceholder: "Reason for rejection",
      inputValidator: (value) => (!value ? "Reason is required." : undefined),
      showCancelButton: true,
      confirmButtonColor: "#c62828",
      confirmButtonText: "Reject",
    });
    if (!result.isConfirmed) return;

    setActioningId(request.id);
    try {
      const updated = await rejectUpatuDisbursement(request.id, result.value);
      setSelectedRequest((prev) => (prev && prev.id === request.id ? normalizeRequest(updated) : prev));
      await loadRequests(true);
      Swal.fire({
        icon: "info",
        title: "Request rejected",
        text: "Member will be notified.",
        confirmButtonColor: brand,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Unable to reject",
        text: err.message || "Please try again.",
        confirmButtonColor: brand,
      });
    } finally {
      setActioningId(null);
    }
  };

  const renderStatusChip = (status) => {
    const meta = statusMeta[status] || { label: status, color: "default" };
    return <Chip size="small" color={meta.color} label={meta.label} />;
  };

  const activeStep = (status) => {
    const meta = statusMeta[status];
    return meta ? meta.step : 0;
  };

  const handleOpenInitiate = () => {
    setInitiateForm((prev) => ({
      ...prev,
      memberId: memberOptions[0]?.id || "",
      amount: "",
    }));
    setInitiateOpen(true);
  };

  const handleInitiateSubmit = async () => {
    const { memberId, amount } = initiateForm;
    if (!memberId || !amount) {
      Swal.fire({
        icon: "warning",
        title: "Missing details",
        text: "Select a member and amount to initiate a payout.",
        confirmButtonColor: brand,
      });
      return;
    }

    try {
      await initiateUpatuDisbursement({ target_member: memberId, amount: Number(amount) });
      await loadRequests(true);
      setInitiateOpen(false);
      Swal.fire({
        icon: "success",
        title: "Initiated",
        text: "Payout request created and pending your approval flow.",
        confirmButtonColor: brand,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Unable to initiate",
        text: err.message || "Please try again.",
        confirmButtonColor: brand,
      });
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isLeadership) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Alert severity="info">
          Upatu disbursements are only accessible to leadership roles (Mwenyekiti, Katibu, Muhasibu).
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "grid", gap: 2.5, p: { xs: 2, md: 3 } }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }}>
        <Stack spacing={0.5}>
          <Typography variant="h5" sx={{ fontWeight: 900, color: brand }}>
            Upatu Disbursements
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            Mwenyekiti initiates payouts to a selected member, then Katibu and Muhasibu approve before settlement.
          </Typography>
        </Stack>
        {role === "Mwenyekiti" && (
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleOpenInitiate}
            disabled={!memberOptions.length}
            sx={{ backgroundColor: brand, "&:hover": { backgroundColor: brandHover } }}
          >
            Initiate payout
          </Button>
        )}
      </Stack>

      {error && (
        <Alert severity="error">
          {error}
        </Alert>
      )}

      <Grid container spacing={2.25} alignItems="stretch" sx={{ mb: 1 }}>
        <Grid item xs={12} md={3}>
          <StatCard
            title="In my queue"
            value={counts.inMyQueue}
            subtitle="Awaiting your action"
            icon={<PendingActionsIcon />}
            index={0}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Ready to disburse"
            value={counts.readyToDisburse}
            subtitle="After Muhasibu approval"
            icon={<CheckCircleOutlineIcon />}
            index={1}
            accent="#2e7d32"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Rejected"
            value={counts.rejected}
            subtitle="Needs resubmission"
            icon={<HighlightOffIcon />}
            index={2}
            accent="#c62828"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Total cycles"
            value={counts.total}
            subtitle="Tracked this month"
            icon={<SwapHorizIcon />}
            index={3}
            accent={brand}
          />
        </Grid>
      </Grid>

      <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between">
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} flex={1}>
              <TextField
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by member, cycle, or reference"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                fullWidth
              />
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Status</InputLabel>
                <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                  <MenuItem value="ALL">All status</MenuItem>
                  {Object.keys(statusMeta).map((key) => (
                    <MenuItem key={key} value={key}>
                      {statusMeta[key].label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <Alert
              icon={<SyncAltIcon fontSize="small" />}
              severity="info"
              sx={{ borderRadius: 2, backgroundColor: "#f8f1f3", color: brand, border: `1px solid ${brand}33`, minWidth: { sm: 260 } }}
            >
              Same chain as withdrawals: Mwenyekiti → Katibu → Muhasibu.
            </Alert>
          </Stack>

          <Divider />

          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Reference</TableCell>
                  <TableCell>Cycle / Member</TableCell>
                  <TableCell>Requested</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Target Account</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginated.map((req, index) => (
                  <TableRow key={req.id} hover component={motion.tr} variants={fadeIn} initial="hidden" animate="visible" custom={index}>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CalendarMonthIcon fontSize="small" color="action" />
                        <Box>
                          <Typography variant="subtitle2">{req.reference || req.id}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Created {req.requestedAt ? new Date(req.requestedAt).toLocaleDateString() : "—"}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2">{req.cycleName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {req.member}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{new Date(req.requestedAt).toLocaleDateString()}</Typography>
                    </TableCell>
                    <TableCell>{formatCurrency(req.amount)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{req.targetAccount || "Account on file"}</Typography>
                    </TableCell>
                    <TableCell>{renderStatusChip(req.status)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Tooltip title="Open details">
                          <IconButton onClick={() => openDetails(req)} size="small">
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canAct(req) && (
                          <>
                            <Tooltip title="Reject">
                              <span>
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  onClick={() => handleReject(req)}
                                  disabled={actioningId === req.id}
                                  sx={{ minWidth: 84 }}
                                >
                                  Reject
                                </Button>
                              </span>
                            </Tooltip>
                            <Tooltip title="Approve and forward">
                              <span>
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => handleApprove(req)}
                                  disabled={actioningId === req.id}
                                  sx={{ backgroundColor: brand, "&:hover": { backgroundColor: brandHover } }}
                                >
                                  Approve
                                </Button>
                              </span>
                            </Tooltip>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {paginated.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
                        <Typography variant="body2">No requests match your filters.</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredRequests.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={closeDetails} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 800 }}>{selectedRequest?.cycleName}</DialogTitle>
        <DialogContent dividers sx={{ display: "grid", gap: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between">
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Reference
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {selectedRequest?.id}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Requested on {selectedRequest && new Date(selectedRequest.requestedAt).toLocaleString()}
              </Typography>
            </Box>
            <Chip label={selectedRequest ? statusMeta[selectedRequest.status]?.label : ""} color={statusMeta[selectedRequest?.status]?.color || "default"} />
          </Stack>

          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Approval chain
              </Typography>
              <Stepper activeStep={selectedRequest ? activeStep(selectedRequest.status) : 0} alternativeLabel>
                {chainSteps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </CardContent>
          </Card>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined" sx={{ borderRadius: 2, height: "100%" }}>
                <CardContent sx={{ display: "grid", gap: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Payout details
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    {selectedRequest && formatCurrency(selectedRequest.amount)}
                  </Typography>
                  <Typography variant="body2">{selectedRequest?.targetAccount || "Account on file"}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Created: {selectedRequest?.requestedAt ? new Date(selectedRequest.requestedAt).toLocaleString() : "—"}
                  </Typography>
                  {selectedRequest?.settledAt && (
                    <Typography variant="body2" color="text.secondary">
                      Settled: {new Date(selectedRequest.settledAt).toLocaleString()}
                    </Typography>
                  )}
                  {selectedRequest?.rejectionReason && (
                    <Typography variant="body2" color="error">
                      Rejection note: {selectedRequest.rejectionReason}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined" sx={{ borderRadius: 2, height: "100%" }}>
                <CardContent sx={{ display: "grid", gap: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Member
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    {selectedRequest?.member}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Requested at {selectedRequest && new Date(selectedRequest.requestedAt).toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ display: "grid", gap: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Workflow history
              </Typography>
              <Stack spacing={1.2}>
                {selectedRequest?.history.map((entry, idx) => (
                  <Stack
                    key={`${entry.action}-${idx}`}
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      {entry.action === "Approved" ? (
                        <CheckCircleOutlineIcon fontSize="small" color="success" />
                      ) : entry.action === "Rejected" ? (
                        <HighlightOffIcon fontSize="small" color="error" />
                      ) : (
                        <PendingActionsIcon fontSize="small" color="action" />
                      )}
                      <Box>
                        <Typography variant="subtitle2">{entry.action}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {entry.by} • {entry.role}
                        </Typography>
                        {entry.note && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            {entry.note}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(entry.at).toLocaleString()}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={closeDetails}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={initiateOpen} onClose={() => setInitiateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800 }}>Initiate Upatu Payout</DialogTitle>
        <DialogContent dividers sx={{ display: "grid", gap: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Target member</InputLabel>
            <Select
              label="Target member"
              value={initiateForm.memberId}
              onChange={(e) => setInitiateForm((prev) => ({ ...prev, memberId: e.target.value }))}
            >
              {memberOptions.map((member) => (
                <MenuItem key={member.id} value={member.id}>
                  {member.name} {member.account ? `• ${member.account}` : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Amount"
            fullWidth
            size="small"
            value={initiateForm.amount ? Number(initiateForm.amount).toLocaleString("en-US") : ""}
            onChange={(e) => {
              const val = e.target.value.replace(/,/g, "");
              if (!isNaN(val)) setInitiateForm((prev) => ({ ...prev, amount: val }));
            }}
            InputProps={{ startAdornment: <InputAdornment position="start">TZS</InputAdornment> }}
          />
          <Alert severity="info" icon={<PendingActionsIcon fontSize="small" />}>
            This creates a payout in the Mwenyekiti queue and routes to Katibu then Muhasibu.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setInitiateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleInitiateSubmit}
            sx={{ backgroundColor: brand, "&:hover": { backgroundColor: brandHover } }}
          >
            Create request
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UpatuDisbursements;
